import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Monitor, X, Check } from "lucide-react";
import { useNotification } from "../../../../hooks/useNotification.jsx";

const MIN_CROP_SIZE = 5;

export default function ScreenshotCapture({ onCapture }) {
  const { addNotification } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cropRect, setCropRect] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setCapturedImage(null);
    setCropRect(null);
    setDragStart(null);
  }, []);

  const handleStartCapture = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      addNotification?.("Trình duyệt không hỗ trợ chụp màn hình", "error");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: "always" },
        audio: false,
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise((r) => requestAnimationFrame(r));
      const offCanvas = document.createElement("canvas");
      offCanvas.width = video.videoWidth;
      offCanvas.height = video.videoHeight;
      offCanvas.getContext("2d").drawImage(video, 0, 0);
      stream.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
      stream = null;
      const img = new Image();
      img.onload = () => {
        setCapturedImage(img);
        setCropRect(null);
        setDragStart(null);
        setIsModalOpen(true);
      };
      img.src = offCanvas.toDataURL("image/png");
    } catch (err) {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (err.name !== "NotAllowedError" && err.name !== "AbortError") {
        addNotification?.(
          "Không chụp được màn hình: " + (err.message || err.name),
          "error"
        );
      }
    }
  };

  useEffect(() => {
    if (!isModalOpen || !capturedImage) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const maxW = container.clientWidth;
    const maxH = container.clientHeight;
    const scale = Math.min(
      maxW / capturedImage.width,
      maxH / capturedImage.height,
      1
    );
    const displayW = Math.max(1, Math.floor(capturedImage.width * scale));
    const displayH = Math.max(1, Math.floor(capturedImage.height * scale));

    canvas.width = displayW;
    canvas.height = displayH;
    canvas.style.width = displayW + "px";
    canvas.style.height = displayH + "px";

    const ctx = canvas.getContext("2d");
    ctx.drawImage(capturedImage, 0, 0, displayW, displayH);

    if (cropRect && cropRect.w > 0 && cropRect.h > 0) {
      const dispScale = displayW / capturedImage.width;
      const dx = cropRect.x * dispScale;
      const dy = cropRect.y * dispScale;
      const dw = cropRect.w * dispScale;
      const dh = cropRect.h * dispScale;
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.fillRect(0, 0, displayW, displayH);
      ctx.drawImage(
        capturedImage,
        cropRect.x,
        cropRect.y,
        cropRect.w,
        cropRect.h,
        dx,
        dy,
        dw,
        dh
      );
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(dx, dy, dw, dh);
      ctx.setLineDash([]);
    } else {
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.fillRect(0, 0, displayW, displayH);
    }
  }, [isModalOpen, capturedImage, cropRect]);

  const getImageCoords = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas || !capturedImage) return null;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const scaleX = capturedImage.width / rect.width;
      const scaleY = capturedImage.height / rect.height;
      return {
        x: Math.max(0, Math.min(capturedImage.width, mouseX * scaleX)),
        y: Math.max(0, Math.min(capturedImage.height, mouseY * scaleY)),
      };
    },
    [capturedImage]
  );

  const handleMouseDown = (e) => {
    const pt = getImageCoords(e);
    if (!pt) return;
    setDragStart(pt);
    setCropRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
  };

  const handleMouseMove = (e) => {
    if (!dragStart) return;
    const pt = getImageCoords(e);
    if (!pt) return;
    setCropRect({
      x: Math.min(dragStart.x, pt.x),
      y: Math.min(dragStart.y, pt.y),
      w: Math.abs(pt.x - dragStart.x),
      h: Math.abs(pt.y - dragStart.y),
    });
  };

  const handleMouseUp = () => {
    setDragStart(null);
  };

  const handleConfirm = useCallback(() => {
    if (
      !capturedImage ||
      !cropRect ||
      cropRect.w < MIN_CROP_SIZE ||
      cropRect.h < MIN_CROP_SIZE
    ) {
      return;
    }
    const w = Math.round(cropRect.w);
    const h = Math.round(cropRect.h);
    const x = Math.round(cropRect.x);
    const y = Math.round(cropRect.y);
    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = w;
    cropCanvas.height = h;
    cropCanvas.getContext("2d").drawImage(capturedImage, x, y, w, h, 0, 0, w, h);
    cropCanvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `screenshot-${Date.now()}.png`, {
          type: "image/png",
        });
        onCapture?.(file);
        handleClose();
      },
      "image/png"
    );
  }, [capturedImage, cropRect, onCapture, handleClose]);

  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen, handleClose, handleConfirm]);

  const isValidCrop =
    !!cropRect && cropRect.w >= MIN_CROP_SIZE && cropRect.h >= MIN_CROP_SIZE;

  return (
    <>
      <button
        type="button"
        className="p-1.5 rounded-md hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-700"
        aria-label="Chụp màn hình"
        title="Chụp màn hình"
        onClick={handleStartCapture}
      >
        <Monitor className="w-4 h-4" />
      </button>
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] bg-black/80 flex flex-col items-center justify-center p-4">
            <div
              ref={containerRef}
              className="flex-1 w-full flex items-center justify-center overflow-hidden min-h-0"
            >
              <canvas
                ref={canvasRef}
                className="cursor-crosshair max-w-full max-h-full select-none"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap justify-center">
              <span className="text-white/90 text-xs">
                {isValidCrop
                  ? `Vùng chọn: ${Math.round(cropRect.w)} × ${Math.round(
                      cropRect.h
                    )} px`
                  : "Kéo chuột để chọn vùng chụp (Esc: huỷ, Enter: xác nhận)"}
              </span>
              <button
                type="button"
                className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm flex items-center gap-1.5"
                onClick={handleClose}
              >
                <X className="w-4 h-4" />
                Huỷ
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-sm flex items-center gap-1.5"
                onClick={handleConfirm}
                disabled={!isValidCrop}
              >
                <Check className="w-4 h-4" />
                Xác nhận
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
